<template>
  <div id="mountain-scene" class="mountain-scene"
    ref="scene"
    :class="{focused: focused}">
    <image-layer class="background" ref="sky"></image-layer>
    <image-layer class="mountain" ref="mountain"></image-layer>
    <foreground-right ref="right" />
    <image-layer class="foreground-left" ref="left"></image-layer>
  </div>
</template>

<script>
import ImageLayer from './ImageLayer.vue'
import ForegroundRight from './ForegroundRight.vue'

export default{
  name: 'mountain-scene',
  components: {
    'image-layer': ImageLayer,
    'foreground-right': ForegroundRight
  },
  data() {
    return {
      focused: false
    }
  },
  props: {
    bound: {
      type: Number,
      default: 0.7
    }
  },
  mounted() {
    this.layers = [
      {name: 'sky'      , ratio: 0.01, element: this.$refs.sky.$el},
      {name: 'mountain' , ratio: 0.02, element: this.$refs.mountain.$el},
      {name: 'left'     , ratio: 0.03, element: this.$refs.left.$el},
      {name: 'right'    , ratio: 0.03, element: this.$refs.right.$el}
    ]
    this.onMouseMove = (e) => requestAnimationFrame(() => this.move(e))
    this.play()
  },
  methods: {
    move(e) {
      const scene = this.$refs.scene
      const w = scene.clientWidth
      const h = scene.clientHeight
      const percentX = e.clientX / window.innerWidth
      const percentY = e.clientY / window.innerHeight
      const centeredX = (percentX * 2) - 1
      const centeredY = (percentY * 2) - 1
      this.layers.forEach((layer) => {
        const x = w * (layer.ratio * centeredX) * -1
        const y = h * (layer.ratio * centeredY) * -1
        // The rotation helps resolve animation rendering issues in Firefox.
        const translation = `translate(${x}px, ${y}px) rotate(0.01deg)`
        layer.element.style.transform = translation
      })

      this.focused = percentY > this.bound
    },
    play() {
      document.addEventListener('mousemove', this.onMouseMove)
    },
    pause() {
      document.removeEventListener('mousemove', this.onMouseMove)
    }
  }
}
</script>

<style lang="scss">
/** mountain-scene (unscoped) **/
@import '../../theme';
@import './util';

// These need to match the ratios defined in the javascript.
$bg-ratio: 0.01;
$md-ratio: 0.02;
$fg-ratio: 0.03;

@mixin scaled-container($ratio: 1) {
  $p: 100% * $ratio * -1;
  position: absolute;
  top    : $p;
  bottom : $p;
  left   : $p;
  right  : $p;

  transition: transform $t-m $e-out-hard;
}

// Keep things scoped to this component
#mountain-scene {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;

  .background {
    @include scaled-container($bg-ratio);

    .top {
      @include layer-image("../../assets/home/sky-blurred_1013x570.jpg");
      // This doesn't trigger for most phones in portrait mode. However,
      // I'm keeping it this way in order to optimize download speed since
      // those devices are probably on slower networks.
      @include media-breakpoint-up(sm) {
        background-image: url("../../assets/home/sky-blurred_1191x670.jpg");
      }
      @include media-breakpoint-up(md) {
        background-image: url("../../assets/home/sky-blurred_1820x1024.jpg");
      }
      @include media-breakpoint-up(lg) {
        background-image: url("../../assets/home/sky-blurred_2040x1148.jpg");
      }
    }
    .bottom {
      @include layer-image("../../assets/home/sky_1013x570.jpg");
      @include media-breakpoint-up(sm) {
        background-image: url("../../assets/home/sky_1191x670.jpg");
      }
      @include media-breakpoint-up(md) {
        background-image: url("../../assets/home/sky_1820x1024.jpg");
      }
      @include media-breakpoint-up(lg) {
        background-image: url("../../assets/home/sky_2040x1148.jpg");
      }
    }
  }

  .mountain {
    @include scaled-container($md-ratio);

    .top {
      @include layer-image("../../assets/home/mountain-blurred_1013x570.png");
      @include media-breakpoint-up(sm) {
        background-image: url("../../assets/home/mountain-blurred_1191x670.png");
      }
      @include media-breakpoint-up(md) {
        background-image: url("../../assets/home/mountain-blurred_1820x1024.png");
      }
      @include media-breakpoint-up(lg) {
        background-image: url("../../assets/home/mountain-blurred_2040x1148.png");
      }
    }
    .bottom {
      @include layer-image("../../assets/home/mountain_1013x570.png");
      @include media-breakpoint-up(sm) {
        background-image: url("../../assets/home/mountain_1191x670.png");
      }
      @include media-breakpoint-up(md) {
        background-image: url("../../assets/home/mountain_1820x1024.png");
      }
      @include media-breakpoint-up(lg) {
        background-image: url("../../assets/home/mountain_2040x1148.png");
      }
    }
  }

  .foreground-left {
    @include scaled-container($fg-ratio);

    .top {
      @include layer-image("../../assets/home/foreground-left-blurred-cropped_1013x570.png", bottom left, 382px 215px);
      @include media-breakpoint-up(sm) {
        background-image: url("../../assets/home/foreground-left-blurred-cropped_1191x670.png");
      }
      @include media-breakpoint-up(md) {
        background-image: url("../../assets/home/foreground-left-blurred-cropped_1820x1024.png");
      }
      @include media-breakpoint-up(lg) {
        background-image: url("../../assets/home/foreground-left-blurred-cropped_2040x1148.png");
      }
    }
    .bottom {
      @include layer-image("../../assets/home/foreground-left-cropped_1013x570.png", bottom left, 382px 215px);
      @include media-breakpoint-up(sm) {
        background-image: url("../../assets/home/foreground-left-cropped_1191x670.png");
      }
      @include media-breakpoint-up(md) {
        background-image: url("../../assets/home/foreground-left-cropped_1820x1024.png");
      }
      @include media-breakpoint-up(lg) {
        background-image: url("../../assets/home/foreground-left-cropped_2040x1148.png");
      }
    }
  }

  .foreground-right {
    display: none;
    @include media-breakpoint-up(md) {
      display: block;
    }
    @include scaled-container($fg-ratio);
  }
}

// Fade Blur
// Fades between two images one on top of the other. This has better performance
// but doesn't look as nice around transparent edges.
body:not(.chrome) {
/* body { */
  #mountain-scene {
    .background, .mountain {
      .top { @include fade-out; }
      .bottom { @include fade-in; }
    }

    .foreground-left, .foreground-right {
      .top { @include fade-in; }
      .bottom { @include fade-out; }
    }

    &.focused {
      .background {
        .top { @include fade-in; }
        // Background can stay visible to help with the content flash.
        .bottom { opacity: 1; }
      }

      .mountain {
        .top { @include fade-in; }
        .bottom { @include fade-out; }
      }

      .foreground-left, .foreground-right {
        .top { @include fade-out; }
        .bottom { @include fade-in; }
      }
    }
  }
}

// Filter Blur
// Uses the CSS `filter: blur` which looks better but doesn't perform as
// well on some browsers.
body.chrome {
  #mountain-scene {
    .background, .mountain {
      .bottom { @include blur-out; }
      .top { display: none; }
    }

    .foreground-left, .foreground-right {
      .bottom { @include blur-in; }
      .top { display: none; }
    }

    &.focused {
      .background, .mountain {
        .bottom { @include blur-in; }
      }

      .foreground-left, .foreground-right {
        .bottom { @include blur-out; }
      }
    }
  }
}
</style>
